import { EXPERIMENTS, PRIVATE_MIRROR, FEED3, FEED_KIND, PREDICT_FEED, RU_FEED, TRANSFERS, type FeedKind } from '../lib/feedContext';
/**
 * Страницы одного бандла: витрина, сканер, коридоры, студия, касса.
 *
 * Маршрутизатора нет и не нужно: страниц пять, а библиотека роутинга — это
 * ещё сто килобайт в бандле, который и так грузится на мобильном интернете
 * перед первым просмотром.
 *
 * База берётся из сборки, а не из `location`: контур `/next/` живёт под
 * префиксом, и адрес сканера там `/next/app`, а на боевом домене — `/app`.
 * Вшитый `/app` работал бы ровно на одном из двух контуров.
 */

import { parked } from './parked';

/** База бандла без хвостового слэша: '' на проде, '/lab' на дубле страницы. */
const base = (import.meta.env.BASE_URL || '/').replace(/\/+$/, '');

/**
 * Собрали ли этот бандл как дубль под `/lab`.
 *
 * Не путь в адресной строке: тестерский бандл с базой `/` тоже откроет
 * `/lab/app`, если Caddy отдал не тот каталог, и тогда касса снова всплывёт
 * в шапке. База вшита на сборке (`VITE_BASE_PATH=/lab/`).
 */
/** Вынесено, чтобы тест не подменял import.meta. */
export function labFromBase(baseUrl: string): boolean {
  return String(baseUrl).replace(/\/+$/, '') === '/lab';
}

/**
 * Вшито на сборке. Тестерский бандл — `false`, и мёртвые ветки с кассой
 * и коридорами вырезает упаковщик, а не условие в рантайме.
 */
export const LAB_BUNDLE = labFromBase(import.meta.env.BASE_URL || '/');

export function isLabBundle(): boolean {
  return LAB_BUNDLE;
}

/**
 * Адрес, на котором владелец просит пробовать кожу, не трогая боевой `/app`.
 *
 * Это не бандл `/lab`: Caddy отдаёт `/labs/app` тем же `index.html`, что и
 * корень, и `isApp` срабатывает по хвосту. Отдельная страница — только путь.
 */
export function labsPath(pathname: string): boolean {
  const p = pathname.replace(/\/+$/, '');
  return p === '/labs/app' || p === '/labs/app/live' || p === '/labs/app/prematch';
}

/**
 * Кожа labs: бандл `/lab` и адрес `/labs/app` на боевой сборке.
 * Полоски купона больше не гейтятся сюда: с 24.08 они и на боевом `/app`.
 */
export function labSkin(pathname: string): boolean {
  return LAB_BUNDLE || labsPath(pathname);
}

/** Класс корня: `nx-root`, плюс `--one` / `--lab`, когда они нужны. */
export function rootClass(pathname: string, one = false, pilot = false): string {
  const bits = ['nx-root'];
  if (one) bits.push('nx-root--one');
  if (pilot) bits.push('nx-root--pilot');
  if (labSkin(pathname)) bits.push('nx-root--lab');
  return bits.join(' ');
}

/** Адрес сканера этого контура. 2.0 и 3.0 живут на своём корне, не на `/app`. */
export const appHref = (): string => (PRIVATE_MIRROR ? '/cupis' : FEED3 ? '/feed3' : EXPERIMENTS ? '/tests' : `${base}/app`);

export const transfersHref = (): string => `${base}/transfers`;

export function isTransfers(pathname: string): boolean {
  return pathname.replace(/\/+$/, '').endsWith('/transfers');
}

export const russkieHref = (): string => `${base}/russkie`;

export function isRusskie(pathname: string): boolean {
  return pathname.replace(/\/+$/, '').endsWith('/russkie');
}

export const predictHref = (): string => `${base}/predict`;

export function isPredict(pathname: string): boolean {
  return pathname.replace(/\/+$/, '').endsWith('/predict');
}

/** Прогноз вилок по часам: сегодня, завтра, неделя, стрим. */
export const forecastHref = (): string => `${base}/forecast`;

export function isForecast(pathname: string): boolean {
  return pathname.replace(/\/+$/, '').endsWith('/forecast');
}

export const eventsHref = (): string => `${base}/events`;

export function isEvents(pathname: string): boolean {
  return pathname.replace(/\/+$/, '').endsWith('/events');
}

/**
 * Лента 2.0 — зеркало BetBurger. Адрес не зависит от `base`: контур живёт на
 * своём `/tests` у Caddy, и на `/lab` его копии нет.
 */
export const experimentsHref = (): string => '/tests';

export function isExperiments(pathname: string): boolean {
  return pathname.replace(/\/+$/, '') === '/tests';
}

/** Лента 3.0 — свой контур /feed3, не копия /tests. */
export const feed3Href = (): string => '/feed3';

export function isFeed3(pathname: string): boolean {
  const path = pathname.replace(/\/+$/, '');
  return path === '/feed3' || path.startsWith('/feed3/');
}

/** Лента 5 — та же /app, фишка сита. Старый /v5/app больше не дверь. */
export const lenta5Href = (): string => `${base}/app/live?tapes=5`;

export function isLenta5(pathname: string, search = ''): boolean {
  if (new URLSearchParams(search).get('tapes') === '5') return true;
  const path = pathname.replace(/\/+$/, '');
  return path === '/v5' || path.startsWith('/v5/');
}

/** Корень зеркала BetBurger, не /feed3/studio и не чужая страница. */
export function isReadyFeed(pathname: string): boolean {
  const path = pathname.replace(/\/+$/, '');
  if (/^\/cupis(?:\/(?:live|prematch))?$/.test(path)) return true;
  if (path === '/tests' || path === '/feed3') return true;
  // Лайв и прематч ленты 2.0/3.0 — тот же экран, не чужая студия.
  if (path === '/tests/live' || path === '/tests/prematch') return true;
  if (path === '/feed3/live' || path === '/feed3/prematch') return true;
  return false;
}

/** Страница лайв-вилок. */
export const liveHref = (): string => PRIVATE_MIRROR ? '/cupis/live' : `${base}/app/live`;

/** Страница прематч-вилок. */
export const prematchHref = (): string => PRIVATE_MIRROR ? '/cupis/prematch' : `${base}/app/prematch`;

/** Какую линию держит адрес сканера. `null` — это не сканер. */
export type PageLine = 'live' | 'prematch';

export function pageLine(pathname: string): PageLine | null {
  if (/^\/cupis\/prematch\/?$/.test(pathname)) return 'prematch';
  if (/^\/cupis\/live\/?$/.test(pathname)) return 'live';
  // parked.linePages прячет две страницы обратно в одну ленту.
  if (parked.linePages) return null;
  const p = pathname.replace(/\/+$/, '');
  if (/(?:^|\/)app\/prematch$/.test(p)) return 'prematch';
  if (/(?:^|\/)app(?:\/live)?$/.test(p)) return 'live';
  return null;
}

/** Адрес студии. `#desk` — открытие окон; `#ads` — стол двух профилей AdsPower. */
export const studioHref = (hash?: string, kind: FeedKind = FEED_KIND): string =>
  `${base}/studio${kind === 'main' ? '' : `?feed=${kind}`}${hash ? `#${hash}` : ''}`;

/** Адрес кассы. */
export const bookHref = (): string => `${base}/book`;

/** Личный отчёт взятых вилок. Не касса. */
export const reportHref = (): string => `${base}/report`;

export function isReport(pathname: string): boolean {
  return pathname.replace(/\/+$/, '').endsWith('/report');
}

/** Запись на стрим 13 октября. Не лента и не дверь входа. */
export function isCup(pathname: string): boolean {
  const clean = pathname.replace(/\/+$/, '');
  return clean.endsWith('/cup') && !clean.endsWith('/cup/race');
}

/** Табло гонки. Только ведущие, адрес не в шапке. */
export function isCupRace(pathname: string): boolean {
  return pathname.replace(/\/+$/, '').endsWith('/cup/race');
}

/** Демо табло: выдуманные игроки, кнопки «вилка» и «колл» — посмотреть анимацию. */
export function isCupRaceDemo(pathname: string): boolean {
  return pathname.replace(/\/+$/, '').endsWith('/cup/race/demo');
}

/** Значок статистики в углу. На табло гонки мешает стриму. */
export function showStatDock(pathname: string): boolean {
  return !isCupRace(pathname) && !isCupRaceDemo(pathname);
}

/** Сухой прогон Polymarket × Cloudbet. Ставок не шлёт. */
export const dryHref = (): string => `${base}/dry`;

export function isDry(pathname: string): boolean {
  return pathname.replace(/\/+$/, '').endsWith('/dry');
}

/** Личный макет ленты: фейковые вилки, без сокета. */
export const uxHref = (): string => `${base}/ux`;

/** Макет карточки: несколько раскладок одной вилки. */
export const uxCardHref = (): string => `${base}/ux/card`;

/**
 * Адрес коридоров.
 *
 * Своя страница, а не режим сканера, и это решение про товар, а не про
 * навигацию. Коридор — вторая ставка с НЕгарантированной прибылью, и всё время,
 * пока он жил полкой под лентой, он делил экран с вилками: свёрнутый — не
 * находился, развёрнутый — читался как продолжение ленты, где прибыль обещана.
 * Отдельный адрес разводит два жанра по разным экранам и заодно даёт коридорам
 * то, чего полке не полагалось: собственные фильтры, порядок и объяснение.
 */
export const midHref = (): string => `${base}/mid`;

/** Адрес первой страницы. */
export const homeHref = (): string => `${base}/`;

/** Сканер ли открыт сейчас. Лайв и прематч — те же экран и сокет, разные адреса. */
export function isApp(pathname: string): boolean {
  return /(?:^|\/)app(?:\/live|\/prematch|\/test)?$/.test(pathname.replace(/\/+$/, ''));
}

/** Ссылка «тест». Тот же сканер и тот же сокет, отдельного потока за адресом нет. */
export const testHref = (): string => `${base}/app/test`;

/** Студия ли открыта сейчас. Всё остальное решает isApp/первая страница. */
export function isStudio(pathname: string): boolean {
  return pathname.replace(/\/+$/, '').endsWith('/studio');
}

/** Касса ли открыта сейчас. */
export function isBook(pathname: string): boolean {
  return pathname.replace(/\/+$/, '').endsWith('/book');
}

/** Коридоры ли открыты сейчас. */
export function isMid(pathname: string): boolean {
  return pathname.replace(/\/+$/, '').endsWith('/mid');
}

/** Вторая дверь: та же витрина на летящей ленте. Боевой корень — по-прежнему `/`. */
export function isLook(pathname: string): boolean {
  return pathname.replace(/\/+$/, '').endsWith('/look');
}

/** Макет /ux: не сканер и не витрина. */
export function isUx(pathname: string): boolean {
  return pathname.replace(/\/+$/, '').endsWith('/ux');
}

/** Макет карточки /ux/card. */
export function isUxCard(pathname: string): boolean {
  return pathname.replace(/\/+$/, '').endsWith('/ux/card');
}

export type AdminTab =
  | 'pulse'
  | 'people'
  | 'journal'
  | 'feed'
  | 'keys'
  | 'access'
  | 'errors'
  | 'calls'
  | 'pump'
  | 'learn'
  | 'curators'
  | 'tickets';

const ADMIN_TABS: readonly AdminTab[] = [
  'pulse',
  'people',
  'journal',
  'feed',
  'keys',
  'access',
  'errors',
  'calls',
  'pump',
  'learn',
  'curators',
  'tickets',
];

/** Стол владельцев. Чужому бандл его монтирует, дверь внутри гасит. */
export const adminHref = (tab?: AdminTab, person?: string): string => {
  if (person) return `${base}/admin/u/${encodeURIComponent(person)}`;
  if (!tab || tab === 'pulse') return `${base}/admin`;
  return `${base}/admin?t=${tab}`;
};

export const learnHref = (): string => `${base}/learn`;

/** Кураторская: свой PWA со своим манифестом и сервис-воркером, scope `/curator`. */
export const curatorHref = (view = ''): string => `${base}/curator${view ? `/${view}` : ''}`;

export function isCurator(pathname: string): boolean {
  return /(?:^|\/)curator(?:\/.*)?$/.test(pathname.replace(/\/+$/, ''));
}

export function isLearn(pathname: string): boolean {
  const p = pathname.replace(/\/+$/, '');
  return /(?:^|\/)learn$/.test(p);
}

/** /learn2 — тестовая страница обучения v2, не трогает /learn. */
export const learn2Href = (): string => `${base}/learn2`;

export function isLearn2(pathname: string): boolean {
  const p = pathname.replace(/\/+$/, '');
  return /(?:^|\/)learn2$/.test(p);
}

export function isAdmin(pathname: string): boolean {
  const p = pathname.replace(/\/+$/, '');
  return /(?:^|\/)admin(?:\/u\/[^/]+)?$/.test(p);
}

export function adminPerson(pathname: string): string | null {
  const m = pathname.replace(/\/+$/, '').match(/(?:^|\/)admin\/u\/([^/]+)$/);
  return m ? decodeURIComponent(m[1] ?? '') : null;
}

export function adminTab(search: string, pathname = ''): AdminTab {
  if (adminPerson(pathname)) return 'people';
  const raw = new URLSearchParams(search).get('t');
  if (raw === 'pump') return 'calls';
  if (raw === 'keys') return 'feed';
  return ADMIN_TABS.find((item) => item === raw) ?? 'pulse';
}

/**
 * Скрытый макет калькулятора: публичная страница, но вне навигации — открыть
 * можно только по прямой ссылке. Путь нарочно неугадываемый и в корне домена.
 */
export const UI_CALC_MOCK_PATH = '/UItest3452345623465234623463262436326';

export function isUiCalcMock(pathname: string): boolean {
  return pathname.replace(/\/+$/, '') === UI_CALC_MOCK_PATH;
}

/**
 * Одна вилка отдельным окном.
 *
 * Окно операционной системы, а не блок на странице: его кладут поверх сайта
 * конторы и держат перед глазами, пока заполняют форму ставки (08.08,
 * владелец — «поверх всех окон, пример на скрине»). Прижать окно поверх прочих
 * умеет система, а не сайт: браузер такого права странице не даёт, и обещать
 * его подписью было бы враньём.
 *
 * Клик из ленты рисует карточку в новом окне из уже живого бандла: так окно
 * открывается сразу и не отбирает место у ленты. Адрес остаётся для ссылки
 * и для случая, когда браузер не отдал ручку окна.
 */
export const forkHref = (id: string): string =>
  `${PRIVATE_MIRROR ? '/cupis' : TRANSFERS ? transfersHref() : RU_FEED ? russkieHref() : PREDICT_FEED ? predictHref() : FEED3 ? feed3Href() : EXPERIMENTS ? experimentsHref() : appHref()}?fork=${encodeURIComponent(id)}`;

/** Какую вилку просили показать отдельно. `null` — обычный сканер. */
export function forkParam(search: string): string | null {
  const id = new URLSearchParams(search).get('fork');
  return id === null || id === '' ? null : id;
}
